'use client';

import { usePathname } from 'next/navigation';
import { useState, useEffect } from 'react';
import type { AuthState } from '@/lib/types/auth';

/**
 * Mobile Navigation Configuration Types
 */
interface MobileNavRoute {
  readonly path: string;
  readonly requiresAuth?: boolean;
  readonly excludeWhenAuth?: boolean;
  readonly allowPartialMatch?: boolean;
}

interface AuthStatus {
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly userRole?: 'tenant' | 'landlord' | null;
}

interface UseMobileNavReturn {
  readonly showMobileNav: boolean;
  readonly authAwareRoutes: readonly string[];
  readonly pathname: string | null;
  readonly authStatus: AuthStatus;
  readonly isAuthRoute: boolean;
  readonly isDashboardRoute: boolean;
}

/**
 * Route Configuration - Authentication Aware Navigation
 */
const PUBLIC_ROUTES: readonly MobileNavRoute[] = [
  // Core public pages
  { path: '/', allowPartialMatch: false },
  { path: '/about', allowPartialMatch: false },
  { path: '/faq', allowPartialMatch: false },
  { path: '/pricing', allowPartialMatch: false },
  { path: '/terms', allowPartialMatch: false },
  { path: '/privacy', allowPartialMatch: false },
  { path: '/cookies', allowPartialMatch: false },
  
  // Product pages
  { path: '/for-landlords', allowPartialMatch: false },
  { path: '/for-tenants', allowPartialMatch: false },
  { path: '/contact', allowPartialMatch: false },
  
  // Property browsing
  { path: '/browse', allowPartialMatch: true },
  { path: '/listings', allowPartialMatch: true },
  { path: '/claim', allowPartialMatch: false },
  
  // Public landlord profiles
  { path: '/landlord', allowPartialMatch: true },
  
  // Security and community pages
  { path: '/vulnerability', allowPartialMatch: false },
  { path: '/bug-bounty', allowPartialMatch: false },
  { path: '/famehall', allowPartialMatch: false },
  
  // Developer and company pages
  { path: '/developers', allowPartialMatch: false },
  { path: '/feature-requests', allowPartialMatch: false },
  { path: '/beta-program', allowPartialMatch: false },
  
  // Special pages
  { path: '/moses', allowPartialMatch: false },
  { path: '/unauthorized', allowPartialMatch: false }
] as const;

const AUTH_ROUTES: readonly MobileNavRoute[] = [
  // Authentication pages
  { path: '/login', excludeWhenAuth: true },
  { path: '/register', excludeWhenAuth: true },
  { path: '/forgot-password', excludeWhenAuth: true },
  { path: '/reset-password', excludeWhenAuth: true }
] as const;

const PROTECTED_ROUTES: readonly MobileNavRoute[] = [
  // Dashboard routes
  { path: '/dashboard', requiresAuth: true },
  
  // Protected feature routes
  { path: '/leave-review', requiresAuth: true },
  { path: '/report', requiresAuth: true },
  
  // Admin and API routes
  { path: '/admin', requiresAuth: true },
  { path: '/api', requiresAuth: true },
  { path: '/auth', requiresAuth: true }
] as const;

/**
 * Authentication-Aware Mobile Navigation Hook
 * 
 * Uses server-provided auth state for optimal performance while preserving
 * all original business logic and route classification behavior.
 */
export function useMobileNav(initialAuth: AuthState): UseMobileNavReturn {
  const pathname = usePathname();
  
  // ✅ Use server-provided auth state (eliminates client-side API calls)
  const authStatus: AuthStatus = {
    isAuthenticated: initialAuth.isAuthenticated,
    isLoading: false,
    userRole: initialAuth.userType === 'tenant' ? 'tenant' : 
             initialAuth.userType === 'landlord' ? 'landlord' : null
  };

  // Route classification
  const isAuthRoute = AUTH_ROUTES.some(route => 
    pathname?.startsWith(route.path) ?? false
  );

  const isDashboardRoute = pathname?.startsWith('/dashboard') ?? false;

  const isProtectedRoute = PROTECTED_ROUTES.some(route => 
    pathname?.startsWith(route.path) ?? false
  );

  const isPublicRoute = PUBLIC_ROUTES.some(route => {
    if (!pathname) return false;
    return route.allowPartialMatch 
      ? pathname.startsWith(route.path) 
      : pathname === route.path;
  });

  // Mobile navigation visibility logic
  const shouldShowMobileNav = (() => {
    if (!pathname || authStatus.isLoading) return false;

    // Never show on protected routes
    if (isProtectedRoute) return false;

    // Hide on auth routes when user is authenticated
    if (isAuthRoute && authStatus.isAuthenticated) return false;

    // Show on public routes
    if (isPublicRoute) return true;

    // Default: hide mobile nav for unknown routes
    return false;
  })();

  // Auth-aware route collection for navigation
  const authAwareRoutes = (() => {
    const routes: string[] = [];
    
    // Add core navigation routes
    PUBLIC_ROUTES.forEach(route => {
      if (['/listings', '/browse', '/'].includes(route.path)) {
        routes.push(route.path);
      }
    });

    // Add auth-specific routes
    if (authStatus.isAuthenticated) {
      routes.push('/dashboard');
      routes.push('/leave-review');
    } else {
      routes.push('/login');
      routes.push('/register');
    }

    return routes;
  })();

  return {
    showMobileNav: shouldShowMobileNav,
    authAwareRoutes,
    pathname,
    authStatus,
    isAuthRoute,
    isDashboardRoute
  } as const;
} 